/**
 * Product lookup utilities for normalized API response
 *
 * The API returns products as dictionaries keyed by ID (FHIR-style pattern)
 * instead of embedding products in menu items. This module provides
 * utilities to resolve ID references to full objects.
 */

// ============================================================================
// Types for normalized API entities
// ============================================================================

export interface IngredientVariationEntry {
  id: string
  name: string
  sort_order?: number
  is_default?: boolean
}

export interface IngredientVariationGroupDef {
  id: string
  name: string
  is_required?: boolean
  variations: IngredientVariationEntry[]
}

export interface Ingredient {
  id: string
  name: string
  standalone_price?: string
  allergy_ids?: string[]
  food_type_ids?: string[]
  translations?: Record<string, unknown>
  image_url?: string
  has_variations?: boolean
  variation_group?: IngredientVariationGroupDef
  variation_price_overrides?: Record<string, string>
}

export interface Allergy {
  id: string
  name: string
  image_url?: string
}

export interface FoodType {
  id: string
  name: string
  image_url?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type MealProduct = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ComboProduct = any

export interface ProductLookup {
  ingredients: Record<string, Ingredient>
  allergies: Record<string, Allergy>
  foodTypes: Record<string, FoodType>
  meals: Record<string, MealProduct>
  combos: Record<string, ComboProduct>
}

export interface ProductsDictionary {
  meals?: Record<string, MealProduct>
  combos?: Record<string, ComboProduct>
  ingredients?: Record<string, Ingredient>
  allergies?: Record<string, Allergy>
  food_types?: Record<string, FoodType>
  categories?: unknown[]
}

// ============================================================================
// Lookup creation
// ============================================================================

/**
 * Create lookup maps from API response products dictionary
 */
export function createProductLookup(products: ProductsDictionary | undefined): ProductLookup {
  return {
    ingredients: products?.ingredients || {},
    allergies: products?.allergies || {},
    foodTypes: products?.food_types || {},
    meals: products?.meals || {},
    combos: products?.combos || {},
  }
}

// ============================================================================
// Ingredient lookups
// ============================================================================

/**
 * Get ingredient by ID from the lookup
 */
export function getIngredientById(
  lookup: ProductLookup,
  ingredientId: string | undefined
): Ingredient | undefined {
  if (!ingredientId) return undefined
  return lookup.ingredients[ingredientId]
}

/**
 * Get ingredient name with fallback
 */
export function getIngredientName(
  lookup: ProductLookup,
  ingredientId: string | undefined
): string {
  const ingredient = getIngredientById(lookup, ingredientId)
  return ingredient?.name || 'Unknown'
}

// ============================================================================
// Allergy and Food Type lookups
// ============================================================================

/**
 * Resolve allergy IDs to allergy objects
 */
export function getAllergies(
  lookup: ProductLookup,
  allergyIds: string[] | undefined
): Allergy[] {
  if (!allergyIds) return []
  return allergyIds
    .map((id) => lookup.allergies[id])
    .filter((a): a is Allergy => a !== undefined)
}

/**
 * Resolve food type IDs to food type objects
 */
export function getFoodTypes(
  lookup: ProductLookup,
  foodTypeIds: string[] | undefined
): FoodType[] {
  if (!foodTypeIds) return []
  return foodTypeIds
    .map((id) => lookup.foodTypes[id])
    .filter((ft): ft is FoodType => ft !== undefined)
}

/**
 * Get all allergy names for an ingredient
 */
export function getIngredientAllergyNames(
  lookup: ProductLookup,
  ingredientId: string | undefined
): string[] {
  const ingredient = getIngredientById(lookup, ingredientId)
  if (!ingredient?.allergy_ids) return []
  return getAllergies(lookup, ingredient.allergy_ids).map((a) => a.name)
}

/**
 * Get all food type names for an ingredient
 */
export function getIngredientFoodTypeNames(
  lookup: ProductLookup,
  ingredientId: string | undefined
): string[] {
  const ingredient = getIngredientById(lookup, ingredientId)
  if (!ingredient?.food_type_ids) return []
  return getFoodTypes(lookup, ingredient.food_type_ids).map((ft) => ft.name)
}

// ============================================================================
// Product lookups (meals, combos)
// ============================================================================

/**
 * Get a meal by ID from the products dictionary
 */
export function getMeal(
  products: ProductsDictionary | undefined,
  id: string
): unknown {
  return products?.meals?.[id]
}

/**
 * Get a combo by ID from the products dictionary
 */
export function getCombo(
  products: ProductsDictionary | undefined,
  id: string
): unknown {
  return products?.combos?.[id]
}

/**
 * Get an ingredient by ID from the products dictionary
 */
export function getIngredient(
  products: ProductsDictionary | undefined,
  id: string
): Ingredient | undefined {
  return products?.ingredients?.[id]
}

/**
 * Get any product by type and ID
 */
export function getProduct(
  products: ProductsDictionary | undefined,
  type: 'meal' | 'combo' | 'ingredient' | string,
  id: string
): unknown {
  if (!products || !id) return undefined

  switch (type) {
    case 'meal':
      return products.meals?.[id]
    case 'combo':
      return products.combos?.[id]
    case 'ingredient':
      return products.ingredients?.[id]
    default:
      return undefined
  }
}

/**
 * Get the product for a menu item using its product_type and product_id
 */
export function getMenuItemProduct(
  products: ProductsDictionary | undefined,
  item: { product_type: string; product_id: string }
): unknown {
  return getProduct(products, item.product_type, item.product_id)
}

/**
 * Get all meals as an array (for iteration)
 */
export function getAllMeals(products: ProductsDictionary | undefined): unknown[] {
  return products?.meals ? Object.values(products.meals) : []
}

/**
 * Get all combos as an array (for iteration)
 */
export function getAllCombos(products: ProductsDictionary | undefined): unknown[] {
  return products?.combos ? Object.values(products.combos) : []
}

/**
 * Get all ingredients as an array (for iteration)
 */
export function getAllIngredients(
  products: ProductsDictionary | undefined
): Ingredient[] {
  return products?.ingredients ? Object.values(products.ingredients) : []
}

/**
 * Check if a product exists in the dictionary
 */
export function hasProduct(
  products: ProductsDictionary | undefined,
  type: 'meal' | 'combo' | 'ingredient' | string,
  id: string
): boolean {
  return getProduct(products, type, id) !== undefined
}
